/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx, cssMap, cssMap as unboundCssMap } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card/card/lazy';
import {
	ActionName,
	SmartLinkTheme,
	SmartLinkPosition,
	ElementName,
} from '@atlaskit/smart-card/enums';
import { MetadataBlock } from '@atlaskit/smart-card/metadata-block';
import { SnippetBlock } from '@atlaskit/smart-card/snippet-block';
import { TitleBlock } from '@atlaskit/smart-card/title-block';
import { token } from '@atlaskit/tokens';

import type { KnowledgeCardSource } from './types';
import { constructSourceUrl } from './utils';

const styles = cssMap({
	container: {},
});

const unboundStyles = unboundCssMap({
	titleBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			font: token('font.body'),
			fontWeight: token('font.weight.semibold'),
			color: token('color.text'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Override visited link color
		"a:visited [data-smart-element='Title']": {
			color: token('color.text'),
		},
	},
	metadataBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'& span': {
			color: token('color.text.subtlest'),
		},
	},
	snippetBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'& span': {
			color: token('color.text.subtle'),
		},
	},
});

interface SourcesSidePanelCardProps {
	index: number;
	onClickHandler: (params: any) => void;
	onError?: (error: any) => void;
	searchSessionId: string;
	source: KnowledgeCardSource;
}

export const SourcesSidePanelCard = ({
	source,
	index,
	onClickHandler,
	onError,
	searchSessionId,
}: SourcesSidePanelCardProps): React.JSX.Element => {
	const handleClick = (e: React.MouseEvent) => {
		const isAuxClick = e.button === 1;
		const isContextMenu = e.button === 2;
		onClickHandler?.({
			index,
			source,
			sourceCardType: 'side-panel-source',
			isAuxClick,
			isContextMenu,
		});
		if (!isContextMenu) {
			e.preventDefault();
			const url = constructSourceUrl({ source, searchSessionId });
			window.open(url, '_blank', 'noopener, noreferrer');
		}
	};

	return (
		<Box
			xcss={styles.container}
			onAuxClick={(e: React.MouseEvent) => {
				if (e.button !== 1) {
					return;
				}
				handleClick(e);
			}}
			onContextMenu={handleClick}
		>
			<Card
				appearance="block"
				showHoverPreview={false}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
				}}
				url={source.url}
				onClick={handleClick}
				onError={onError || (() => {})}
			>
				<TitleBlock
					css={unboundStyles.titleBlockCss}
					maxLines={1}
					actions={[
						{ name: ActionName.CopyLinkAction, hideContent: true },
						{ name: ActionName.PreviewAction, hideContent: true },
					]}
					showActionOnHover={true}
					position={SmartLinkPosition.Center}
				/>
				<MetadataBlock
					css={unboundStyles.metadataBlockCss}
					primary={[
						{ name: ElementName.ModifiedOn },
						{ name: ElementName.ModifiedBy },
						{ name: ElementName.OwnedBy },
					]}
					maxLines={1}
				/>
				<SnippetBlock css={unboundStyles.snippetBlockCss} maxLines={2} />
			</Card>
		</Box>
	);
};
